﻿import { FnWithNumberInput, PositiveNumber } from '../Types';
import { useCallback } from 'react';
import { useSliderContext } from '../SliderContext';

type UseGroupManagementProps = {
  maxGroupSize: PositiveNumber,
  onGroupSizeChange: FnWithNumberInput,
  slideBy: number
  spaceBetweenTiles: number
}

const useGroupManagement = (
  {
    onGroupSizeChange,
    slideBy,
    maxGroupSize,
    spaceBetweenTiles
  }: UseGroupManagementProps) => {
  const { parentRect, tiles, groupSize, tileWidth, slideAmount } = useSliderContext();

  const setGroupSize = useCallback(() => {
    if (!parentRect.current || !tiles.current || !tileWidth) {
      return;
    }

    let newGroupSize = 1;

    for (let i = tiles.current.length; i > 0; i--) {
      const currentGroupWidth = Math.floor((tileWidth * i) + (spaceBetweenTiles * (i - 1)));
      if (currentGroupWidth <= parentRect.current.width) {
        newGroupSize = Math.min(i, maxGroupSize);
        break;
      }
    }

    if (groupSize.current !== newGroupSize) {
      groupSize.current = newGroupSize;
      onGroupSizeChange(newGroupSize);
    }
  }, [maxGroupSize, spaceBetweenTiles, tileWidth, onGroupSizeChange, parentRect, tiles, groupSize]);

  const updateSlideAmount = useCallback(() => {
    if (slideBy < 0) {
      slideAmount.current = Math.max(groupSize.current + slideBy, 1);
    } else {
      slideAmount.current = Math.min(groupSize.current, slideBy);
    }
  }, [slideBy, groupSize, slideAmount]);

  return {
    groupSize,
    slideAmount,
    setGroupSize,
    updateSlideAmount
  };
};

export default useGroupManagement;